// ============================================
// 场景保存（2026-09-27）
// 背景：保存逻辑原先内联在 SceneToolbar，无法被快捷键复用（Ctrl+S 会触发浏览器"保存网页"）。
// 方案：抽为 hook——内部用 getState 取最新状态（可在键盘事件/任意组件中调用，无闭包过期问题），
//       返回 { save, saving }；SceneToolbar 保存按钮与编辑器 Ctrl+S 共用。
// ============================================

import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';
import useSceneStore from '../stores/sceneStore';
import useLayerStore from '../stores/layerStore';
import useEditorUiStore from '../stores/editorUiStore';
import useDashboardStore from '../stores/dashboardStore';
import useGisStore from '../stores/gisStore';
import { useDataBindingStore } from '../stores/dataBindingStore';
import { sceneThumbnailRegistry } from '../stores/sceneThumbnailStore';
import { useSceneBindingStore } from '../stores/sceneBindingStore';
import { updateScene } from '../api/scenes';
import { updateModel as apiUpdateModel } from '../api/models';
import { getChart3DSnapshots } from '../utils/chart3dSnapshot';

export function useSceneSave() {
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  /** 保存场景（含关联大屏双保存）；返回是否成功。可安全连按（进行中直接忽略） */
  const save = useCallback(async (): Promise<boolean> => {
    const st = useSceneStore.getState();
    const currentScene = st.currentScene;
    if (!currentScene) {
      message.error('当前无场景可保存');
      return false;
    }
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      // 缩略图：通过 SceneCanvas 注册的抓帧函数（render 一帧后 toDataURL），保证非黑帧
      let thumbnail: string | undefined;
      try {
        thumbnail = sceneThumbnailRegistry.capture?.() || undefined;
      } catch {
        thumbnail = undefined;
      }
      await updateScene(currentScene.id, {
        name: currentScene.name,
        thumbnail,
        config: {
          ...currentScene.config,
          environment: st.environment,
          effects: st.effects,
          lights: st.lights,
          // 2026-10-17（体积元素 S1）：空间效果体（区域覆盖/光柱几何随场景保存）
          volumes: st.volumes,
          // 2026-10-17（标绘层 S2）：3D 标绘对象
          annotations: st.annotations,
          // S0-A：场景内 3D 图表原型（验证保存/预览同源）
          spikeChart3D: st.spikeChart3D,
          // S0-B/S0-C：图表贴屏 + Html HUD
          spikeBoard3D: st.spikeBoard3D,
          spikeHud3D: st.spikeHud3D,
          // P0：多图表对象
          chart3dObjects: st.chart3dObjects,
          // P0 发布冻结：保存时快照当前图表数据；预览/发布优先使用该快照
          chart3dSnapshots: getChart3DSnapshots(),
          boundedMode: st.boundedMode,
          // 2026-10-06：展示区域（框选有限展示范围）随工程保存；预览/发布同一渲染
          displayRegion: st.displayRegion,
          // 2026-10-07：模型轴心修正偏移（第三方模型视觉中心≠原点）
          pivotOffsets: st.pivotOffsets,
          showGrid: st.showGrid,
          showGround: st.showGround,
          markers: st.markers,
          flyLines: st.flyLines,
          waypoints: st.waypoints,
          browseRoute: st.browseRoute,
          carousel: st.carousel,
          clipping: st.clipping,
          measurements: st.measurements,
          layers: useLayerStore.getState().layers,
          cameraStrategy: st.cameraStrategy,
          savedCamera: st.savedCamera,
          pathCameras: st.pathCameras,
          // 2026-10-15（P0-④）：鸟瞰相机配置 + 主相机设置（编辑器/预览/发布同源）
          birdsEyeCamera: st.birdsEyeCamera,
          mainCamera: st.mainCamera,
          // I1：仿真参数包（流体/能耗）随工程保存
          simulation: st.simulation,
          // 2026-09-08｜阶段5：组合模型构件业务配置（显隐/变换/材质）随工程保存
          subObjectConfigs: st.subObjectConfigs,
          // 2026-09-27（审计 FE-F08 修复）：数据源与属性绑定随场景保存（原仅内存，刷新丢失）
          datasources: useDataBindingStore.getState().datasources,
          bindings: useDataBindingStore.getState().bindings,
          // 2026-09-27（审计 FE-H08 修复）：GIS 图层/围栏/热力图/视图与相机随场景保存（原仅内存）
          gis: {
            gisState: useGisStore.getState().gisState,
            geoFences: useGisStore.getState().geoFences,
            heatmaps: useGisStore.getState().heatmaps,
          },
        },
      } as any);
      // 2026-09-08｜调试：保存时构件配置条数（确认落库数据）
      console.log('[SAVE_SUBOBJECTS]', Object.entries(st.subObjectConfigs).map(([mid, arr]) => `${mid.slice(0, 6)}:${arr.length}`).join(', '));
      await Promise.all(
        st.models.map((m) => apiUpdateModel(m.id, m as any).catch(() => {})),
      );
      // 阶段4｜G3：保存时 flush 模型↔设备绑定（device-link），刷新后可恢复
      await useSceneBindingStore.getState().flushDeviceLinks(currentScene.id, st.models).catch(() => false);
      // B-07 双保存：3D 保存后顺带保存关联大屏（若脏）——顺序：先场景后大屏，大屏失败不阻塞但明确提示
      let dashAttempted = false;
      let dashOk = true;
      const dashId = useEditorUiStore.getState().dashboardId;
      const dashState = useDashboardStore.getState();
      if (dashId && dashState.currentPage && dashState.dirty) {
        dashAttempted = true;
        dashOk = await dashState.savePage();
      }
      useSceneStore.getState().setDirty(false);
      if (!dashAttempted) {
        message.success('场景已保存');
      } else if (dashOk) {
        message.success('场景与大屏已保存');
      } else {
        message.warning('场景已保存；大屏保存失败，请在大屏编辑中重试');
      }
      return true;
    } catch {
      message.error('保存失败');
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, []);

  return { save, saving };
}
